/**
 * @component SVCSPD001_11TB
 * @title 계좌 > 스피드조회 등록 > 계좌조회 탭
 * @description 계좌 > 스피드조회 등록 > 계좌조회 탭
 * @asis H95101A
 * @properties
 * @linked-menu
 * @author 곽동현
 * @history
 *   [2026-04-21][곽동현] 초기 작성
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageType, useGlobalErrorStore, usePageReady, useThrottle } from 'scbk-services';
import { useSvcGstSetSetTransactionHistorySessionCleanup } from 'scbk-servicing/hooks/api';
import {
    useSvcGstLstListDemandAccountTransactionInquiry,
    useSvcGstLstListDepositandFundTransactionInquiry,
    useSvcGstLstListForeignCurrencyTransactionInquiry,
} from 'scbk-servicing/hooks/api/svcGstFindListHooks';
import {
    SvcGstLstListDemandAccountTransactionInquiryResponse,
    SvcGstLstListForeignCurrencyTransactionInquiryResponse,
} from 'scbk-servicing/types/api';
import { depTrxDetailList } from 'scbk-servicing/types/api/svcGstLstListDemandAccountTransactionInquiry';
import {
    SvcGstLstListDepositandFundTransactionInquiryResponse,
    depDetailList,
} from 'scbk-servicing/types/api/svcGstLstListDepositandFundTransactionInquiry';
import { forcurTrxDetailList } from 'scbk-servicing/types/api/svcGstLstListForeignCurrencyTransactionInquiry';
import {
    Dialog,
    SecureKeypadFieldOption,
    dateUtils,
    filterBuilder,
    formatUtils,
    rulesBuilder,
    useExcelDownload,
    usePageForm,
    useSecureKeypad,
    withPageComponent,
} from 'scbk-shared-module';
import {
    AccordionUnder,
    BulletList,
    BulletListItem,
    Button,
    ButtonWithIcon,
    DatePickerRange,
    FieldRound,
    FieldWrapper,
    LineIcon,
    NativeSelect,
    NativeSelectOption,
    Table,
    TableBody,
    TableCaption,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
    cn,
} from 'scbk-ui-web';

export default withPageComponent(
    'SVCSPD001_11TB',
    {
        type: PageType.MAIN_SCREEN,
        layoutOptions: {
            description: '계좌 > 스피드조회 등록 > 계좌조회 탭',
        },
    },
    function () {
        const { t } = useTranslation();
        const [term, setTerm] = useState('1');
        const [flag, setFlag] = useState('');
        const acctNumRef = useRef<HTMLInputElement>(null); // 계좌번호
        const birthNumRef = useRef<HTMLInputElement>(null); // 생년월일
        const [acctNoValue, setAcctNoValue] = useState<string>('');
        const [birthNumValue, setBirthNumValue] = useState<string>('');
        const [rangeDate, setRangeDate] = useState<{
            start: string;
            end: string;
            limitEnd: string;
            limitStart: string;
        }>({
            start: formatUtils.toDate(dateUtils.getToday(), '.'),
            end: formatUtils.toDate(dateUtils.getToday(), '.'),
            limitEnd: formatUtils.toDate(dateUtils.getToday(), '/'),
            limitStart: formatUtils.toDate(dateUtils.getToday(), '/'),
        });
        const { setErrorInfo } = useGlobalErrorStore();

        const currYear = Number(dateUtils.getYear());

        const [initData, setInitTransactionData] =
            useState<SvcGstLstListDepositandFundTransactionInquiryResponse>();

        // 예금펀드 List
        const [initDataList, setInitTransactionDataList] = useState<depDetailList[]>([]);

        const [initDepositData, setInitDepositTransactionData] =
            useState<SvcGstLstListDemandAccountTransactionInquiryResponse>();

        // 요구불 List
        const [initDepositDataList, setInitDepositTransactionDataList] = useState<
            depTrxDetailList[]
        >([]);

        const [initForeignData, setInitForeignTransactionData] =
            useState<SvcGstLstListForeignCurrencyTransactionInquiryResponse>();

        // 외화 List
        const [initForeignDataList, setInitForeignTransactionDataList] = useState<
            forcurTrxDetailList[]
        >([]);

        // 거래내역세션정리
        const { request: getInitSessionCleanup } =
            useSvcGstSetSetTransactionHistorySessionCleanup();

        // 예금펀드거래내역조회
        const {
            requestWithSecure: getInitTransactionData,
            isLoading: isLoadingGetInitTransactionData,
        } = useSvcGstLstListDepositandFundTransactionInquiry({
            onError: async (error) => {
                setFlag('');
                setErrorInfo({ ...error });
            },
        });

        // 요구불거래내역조회
        const {
            requestWithSecure: getInitDemandTransactionData,
            isLoading: isLoadingGetInitDemandTransactionData,
        } = useSvcGstLstListDemandAccountTransactionInquiry({
            onError: async (error) => {
                setFlag('');
                setErrorInfo({ ...error });
            },
        });

        // 외화거래내역조회
        const {
            requestWithSecure: getInitForeignTransactionData,
            isLoading: isLoadingGetInitForeignTransactionData,
        } = useSvcGstLstListForeignCurrencyTransactionInquiry({
            onError: async (error) => {
                setFlag('');
                setErrorInfo({ ...error });
            },
        });

        const outSumAmt =
            initDepositDataList.reduce((sum, data) => {
                return data.depPayPart === '2' ? sum + Number(data.trxAmt) : sum;
            }, 0) || 0;

        const inSumAmt =
            initDepositDataList.reduce((sum, data) => {
                return data.depPayPart !== '2' ? sum + Number(data.trxAmt) : sum;
            }, 0) || 0;

        const outForeignSumAmt =
            initForeignDataList.reduce((sum, data) => {
                return data.trxPart !== '입금' &&
                    data.trxPart !== '예금이자' &&
                    data.trxPart !== '원가입금'
                    ? sum + Number(data.trxAmt)
                    : sum;
            }, 0) || 0;

        const inForeignSumAmt =
            initForeignDataList.reduce((sum, data) => {
                return data.trxPart === '입금' ||
                    data.trxPart === '예금이자' ||
                    data.trxPart === '원가입금'
                    ? sum + Number(data.trxAmt)
                    : sum;
            }, 0) || 0;

        /**
         * 키패드 정보 설정
         */
        const secureKeypadList: SecureKeypadFieldOption[] = [
            {
                fieldId: 'keypadNum', // input id
            },
        ];

        //* 보안키패드 객체 */
        const secureKeypad = useSecureKeypad(secureKeypadList);

        // 입력필드
        interface formData {
            accountNum: string;
            keypadNum: string;
            birthNum: string;
        }

        const { useFields } = usePageForm<formData>({});

        const fields = useFields({
            accountNum: {
                filters: filterBuilder().build(),
                rules: rulesBuilder<formData>()
                    .maxLength(11)
                    .validate((v, ctx) => {
                        const el = document.getElementById(ctx.name) as HTMLInputElement;
                        const value = v !== '' ? v : (el?.value ?? '');

                        if (value.length === 0) {
                            return t('SVCSPD001_11TB.047', '계좌번호를 입력해 주세요.');
                        } else if (value.length !== 11) {
                            return t('SVCSPD001_11TB.048', '계좌번호 11자리를 입력해 주세요.');
                        }

                        return true;
                    })
                    .build(),
            },
            keypadNum: {
                filters: filterBuilder().build(),
                rules: rulesBuilder<formData>()
                    .maxLength(4)
                    .validate((v, ctx) => {
                        const el = document.getElementById(ctx.name) as HTMLInputElement;
                        const value = v !== '' ? v : (el?.value ?? '');

                        if (value.length === 0) {
                            return t('SVCSPD001_11TB.049', '계좌비밀번호를 입력해 주세요.');
                        }

                        return value.length === 4
                            ? true
                            : t('SVCSPD001_11TB.052', '계좌비밀번호 4자리를 입력해 주세요.');
                    })
                    .build(),
            },
            birthNum: {
                filters: filterBuilder().build(),
                rules: rulesBuilder<formData>()
                    .maxLength(6)
                    .validate((v, ctx) => {
                        const el = document.getElementById(ctx.name) as HTMLInputElement;
                        const value = v !== '' ? v : (el?.value ?? '');

                        return value.length === 6
                            ? true
                            : t('SVCSPD001_11TB.050', '생년월일을 입력해 주세요.');
                    })
                    .build(),
            },
        });

        // 더보기 클릭시
        const serchAddBtn = useThrottle(async (addFlag: string) => {
            // 요구불
            if (addFlag === '1') {
                const res = await getInitDemandTransactionData({
                    acctNum: initDepositData?.acctNum ?? '',
                    referStartDay: initDepositData?.referStartDay ?? '',
                    referEndDay: initDepositData?.referEndDay ?? '',
                    perNo: initDepositData?.perNo ?? '',
                    kdate: initDepositData?.kdate ?? '',
                    ktrxNum: initDepositData?.ktrxNum ?? '',
                    ktrxBranch: initDepositData?.ktrxBranch ?? '',
                    kaddCode: initDepositData?.kaddCode ?? '',
                    ktime: initDepositData?.ktime ?? '',
                    processTp: initDepositData?.processTp ?? '',
                });

                setInitDepositTransactionData(res);
                setInitDepositTransactionDataList((prev) => [
                    ...prev,
                    ...(res?.depTrxDetailRecord ?? []),
                ]);
            } else if (addFlag === '2') {
                // 펀드
                const res = await getInitTransactionData({
                    acctNum: initData?.acctNum ?? '',
                    referStartDay: initData?.referStartDay ?? '',
                    referEndDay: initData?.referEndDay ?? '',
                    perNo: initData?.perNo ?? '',
                    kdate: initData?.kdate ?? '',
                    ktrxNum: initData?.ktrxNum ?? '',
                    ktrxBranch: initData?.ktrxBranch ?? '',
                    kaddCode: initData?.kaddCode ?? '',
                    ktime: '',
                    processTp: initData?.processTp ?? '',
                });

                setFlag('1'); // 1:적립식예금펀드, 2:요구불, 3:외화명세조회, 4:61

                setInitTransactionData(res);
                setInitTransactionDataList((prev) => [...prev, ...(res?.depDetailRecordlim ?? [])]);
            } else if (addFlag === '3') {
                // 외화
                const res = await getInitForeignTransactionData({
                    acctNum: initForeignData?.acctNum ?? '',
                    referStartDay: initForeignData?.startDate ?? '',
                    referEndDay: initForeignData?.endDate ?? '',
                    perNo: initForeignData?.perNo ?? '',
                    kdate: initForeignData?.kdate ?? '',
                    ktrxNum: initForeignData?.ktrxNum ?? '',
                    ktrxBranch: '',
                    kaddCode: '',
                    ktime: '',
                    processTp: initForeignData?.processTp ?? '',
                });

                setFlag('3'); // 1:적립식예금펀드, 2:요구불, 3:외화명세조회, 4:61

                setInitForeignTransactionData(res);
                setInitForeignTransactionDataList((prev) => [
                    ...prev,
                    ...(res?.forcurTrxDetailRecord ?? []),
                ]);
            }
        }, 1000);

        // 조회버튼 클릭시
        const searchBtn = useThrottle(async () => {
            let startDate = '';
            let endDate = '';
            let acctNoGn = 0;
            const acctNo = acctNumRef.current?.value ?? '';
            const birthNum = birthNumRef.current?.value ?? '';
            const el = document.getElementById('keypadNum') as HTMLInputElement;
            const keypadNum = el?.value ?? '';

            if (!acctNo) {
                Dialog.alert({
                    content: t('SVCSPD001_11TB.047', '계좌번호를 입력해 주세요.'),
                    onClick: async () => {
                        requestAnimationFrame(() => {
                            acctNumRef.current?.focus();
                        });
                    },
                });

                return false;
            }

            if (acctNo.length !== 11) {
                Dialog.alert({
                    content: t('SVCSPD001_11TB.048', '계좌번호 11자리를 입력해 주세요.'),
                    onClick: async () => {
                        requestAnimationFrame(() => {
                            acctNumRef.current?.focus();
                        });
                    },
                });

                return false;
            }

            if (!keypadNum) {
                Dialog.alert({
                    content: t('SVCSPD001_11TB.049', '계좌비밀번호를 입력해 주세요.'),
                    onClick: async () => {
                        requestAnimationFrame(() => {
                            secureKeypad.open('keypadNum');
                        });
                    },
                });

                return false;
            }

            if (keypadNum.length !== 4) {
                Dialog.alert({
                    content: t('SVCSPD001_11TB.052', '계좌비밀번호 4자리를 입력해 주세요.'),
                    onClick: async () => {
                        requestAnimationFrame(() => {
                            secureKeypad.open('keypadNum');
                        });
                    },
                });

                return false;
            }

            if (birthNum.length !== 6) {
                Dialog.alert({
                    content: t('SVCSPD001_11TB.050', '생년월일을 입력해 주세요.'),
                    onClick: async () => {
                        requestAnimationFrame(() => {
                            birthNumRef.current?.focus();
                        });
                    },
                });

                return false;
            }

            if (acctNo !== '') {
                acctNoGn = Number(acctNo.substring(3, 5));
            }

            if (term === '1') {
                // 당일
                startDate = dateUtils.getAddDays(0);
                endDate = dateUtils.getAddDays(0);
            } else if (term === '2') {
                // 3일
                startDate = dateUtils.getAddDays(-3);
                endDate = dateUtils.getAddDays(0);
            } else if (term === '3') {
                // 일주
                startDate = dateUtils.getAddDays(-7);
                endDate = dateUtils.getAddDays(0);
            } else if (term === '4') {
                // 1개월
                startDate = dateUtils.getAddMonths(-1);
                endDate = dateUtils.getAddDays(0);
            } else if (term === '5') {
                // 3개월
                startDate = dateUtils.getAddMonths(-3);
                endDate = dateUtils.getAddDays(0);
            } else if (term === '6') {
                // 기간설정
                startDate = (rangeDate?.start ?? '').replaceAll('.', '');
                endDate = (rangeDate?.end ?? '').replaceAll('.', '');
            }

            if (dateUtils.getAddMonths(3, startDate) < endDate) {
                Dialog.alert({
                    content: t(
                        'SVCSPD001_11TB.073',
                        '해당 거래는 최근 3개월까지만 조회 할 수 있어요. 조회기간을 다시 설정해 주세요.',
                    ),
                    onClick: async () => {
                        requestAnimationFrame(() => {
                            document.getElementById('pickerDate')?.focus();
                            // birthNumRef.current?.focus();
                        });
                    },
                });

                return false;
            }

            // 적립식 예금 - 펀드
            if (
                acctNoGn === 46 ||
                acctNoGn === 48 ||
                acctNoGn === 49 ||
                acctNoGn === 70 ||
                acctNoGn === 90
            ) {
                const res = await getInitTransactionData({
                    acctNum: acctNo,
                    referStartDay: startDate,
                    referEndDay: endDate,
                    perNo: birthNum,
                    kdate: '',
                    ktrxNum: '',
                    ktrxBranch: '',
                    kaddCode: '',
                    ktime: '',
                    processTp: 'F',
                });

                setFlag('1'); // 1:적립식예금펀드, 2:요구불, 3:외화명세조회, 4:61

                setInitTransactionData(res);
                setInitTransactionDataList(res?.depDetailRecordlim ?? []);
            } else if (acctNoGn <= 60 || acctNoGn === 80) {
                // 요구불
                const res = await getInitDemandTransactionData({
                    acctNum: acctNo,
                    referStartDay: startDate,
                    referEndDay: endDate,
                    perNo: birthNum,
                    kdate: '',
                    ktrxNum: '',
                    ktrxBranch: '',
                    kaddCode: '',
                    ktime: '',
                    processTp: 'F',
                });

                setFlag('2'); // 1:적립식예금펀드, 2:요구불, 3:외화명세조회, 4:61

                setInitDepositTransactionData(res);
                setInitDepositTransactionDataList(res?.depTrxDetailRecord ?? []);
            } else if (acctNoGn >= 85 && acctNoGn <= 89) {
                // 외화명세조회
                const res = await getInitForeignTransactionData({
                    acctNum: acctNo,
                    referStartDay: startDate,
                    referEndDay: endDate,
                    perNo: birthNum,
                    kdate: '',
                    ktrxNum: '',
                    ktrxBranch: '',
                    kaddCode: '',
                    ktime: '',
                    processTp: 'F',
                });

                setFlag('3'); // 1:적립식예금펀드, 2:요구불, 3:외화명세조회, 4:61

                setInitForeignTransactionData(res);
                setInitForeignTransactionDataList(res?.forcurTrxDetailRecord ?? []);
            } else if (acctNoGn === 61) {
                const res = await getInitTransactionData({
                    acctNum: acctNo,
                    referStartDay: startDate,
                    referEndDay: endDate,
                    perNo: birthNum,
                    kdate: '',
                    ktrxNum: '',
                    ktrxBranch: '',
                    kaddCode: '',
                    ktime: '',
                    processTp: 'F',
                });

                setFlag('4'); // 1:적립식예금펀드, 2:요구불, 3:외화명세조회, 4:61

                setInitTransactionData(res);
                setInitTransactionDataList(res?.depDetailRecordlim ?? []);
            } else {
                return false;
            }

            secureKeypad.clear();

            // 비밀번호 필드 초기화
            fields.keypadNum.inputProps.onChange?.({
                target: {
                    value: '',
                    name: 'keypadNum',
                },
            } as any);
        }, 1000);

        useEffect(() => {}, [flag]);

        const mergeRef = useRef<HTMLTableElement>(null);
        const tableRef = useRef<HTMLTableElement>(null);
        const tableRef2 = useRef<HTMLTableElement>(null);
        const tableRef3 = useRef<HTMLTableElement>(null);
        const { downloadTableToExcel } = useExcelDownload();

        // 엑셀다운로드
        const handleDownloadClick = useCallback(async () => {
            // let exName = 'speed_search_' + dateUtils.toTimeString(new Date());
            let exName = 'speed_search';

            await downloadTableToExcel(mergeRef.current, {
                fileName: exName,
                sheetName: exName,
            });
        }, [mergeRef.current, downloadTableToExcel]);

        // 인쇄
        const handlePrintClick = useCallback(() => {
            if (!mergeRef.current) {
                return false;
            }

            const printWindow = window.open('', '_blank');

            if (!printWindow) {
                return false;
            }

            printWindow.document.write(`
                <html>
                    <head>
                        <title>인쇄</title>
                        <style>
                            * {margin: 0; padding; 0;}
                            body {font-family: "SCProsperSans", "NotoSans Kr"; padding: 20px;}

                            table {
                                position: relative;
                                width: 100%;
                                border-collapse: collapse;
                                overflow: auto;
                                border-top: 1px solid #858687;
                                margin-top: 16px;
                            }
                            
                            table caption {
                                position: absolute;
                                width: 1px;
                                height: 1px;
                                padding: 0;
                                margin: -1px;
                                overflow: hidden;
                                clip: rect(0, 0, 0, 0);
                                white-space: nowrap;
                                border-width: 0;
                            }

                            table th {
                                text-align: left;
                                padding: 12px 16px;
                                vertical-align: middle;
                                font-size: 14px;
                                line-height: 1.5;
                                color: #191919;
                                font-weight: 500;
                                border-bottom: 1px solid #eeeeee;
                                background-color: #f8f8f8;
                            }

                            table td {
                                padding: 12px;
                                vertical-align: middle;
                                font-size: 14px;
                                line-height: 1.5;
                                text-align: left;
                                border-left: 1px solid #eeeeee;
                                border-bottom: 1px solid #eeeeee;
                                background-color: #fff;
                            }

                            table td.text-left {
                                text-align: left;
                            }
                            
                            table td.text-right {
                                text-align: right;
                            }

                            table td.text-center {
                                text-align: center;
                            }

                            table th + th,
                            table td + td {
                                border-left: 1px solid #eeeeee;
                            }

                            table tr th:first-child,
                            table tr td:first-child {
                                border-left: none;
                            }

                            .print-table__col th:first-child,
                            .print-table__col td:first-child {
                                width: 150px;
                            }

                            .print-hidden {
                                display: none;
                            }

                            .total {
                                margin-top: 12px;
                                text-align: right;
                                list-style: none;
                            }

                            .total li {
                                font-size: 14px;
                                line-height: 1.5;
                                color: #666666;
                            }

                            .total li strong {
                                color: #2772c7;
                                margin-left: 8px;
                            }

                            .total li:nth-child(2) strong {
                                color: #d0021b;
                            }

                            @media print {
                            * {print-color-adjust: exact !important;}
                            }
                        </style>
                    </head>
                    
                    <body>
                        ${mergeRef.current?.innerHTML}
                    </body>
                </html>
            `);

            printWindow.document.close();

            setTimeout(() => {
                printWindow.focus();
                printWindow.print();
            }, 500);
        }, []);

        usePageReady(async () => {
            await getInitSessionCleanup({});
        });

        return (
            <>
                {/* 260417 클래스 삭제 */}
                <div className="flex flex-col gap-8">
                    <FieldWrapper
                        labelFor="accountNum"
                        title={t('SVCSPD001_11TB.004', '계좌번호')}
                        // isError={isError('accountNum')}
                        // helpMessage={getFieldError('accountNum')}
                    >
                        <FieldRound
                            // {...fields.accountNum.inputProps}
                            id="accountNum"
                            placeholder={t('SVCSPD001_11TB.005', '- 없이 숫자만 입력')}
                            title={t('SVCSPD001_11TB.075', '계좌번호 - 없이 숫자만 입력')}
                            aria-label={t('SVCSPD001_11TB.075', '계좌번호 - 없이 숫자만 입력')}
                            inputMode="numeric"
                            maxLength={11}
                            value={acctNoValue}
                            ref={acctNumRef}
                            onChange={(e) => {
                                const onlyNumbers = e.target.value.replace(/[^0-9]/g, '');

                                fields.accountNum.inputProps.onChange?.({
                                    ...e,
                                    target: {
                                        ...e.target,
                                        value: onlyNumbers,
                                    },
                                });

                                setAcctNoValue(onlyNumbers);
                            }}
                            // error={isError('accountNum')}
                        />
                    </FieldWrapper>
                    <FieldWrapper
                        labelFor="keypadNum"
                        title={t('SVCSPD001_11TB.006', '계좌비밀번호')}
                        // isError={isError('keypadNum')}
                        // helpMessage={getFieldError('keypadNum')}
                    >
                        <FieldRound
                            // {...fields.keypadNum.inputProps}
                            id="keypadNum"
                            type="password"
                            data-enc="num" // 가상키패드 입력 타입 'num' : 숫자 키패드 / 'qwerty' : 문자키패드
                            inputMode="numeric"
                            maxLength={4}
                            placeholder={t('SVCSPD001_11TB.007', '숫자 4자리')}
                            title={t('SVCSPD001_11TB.007', '숫자 4자리')}
                            // error={isError('keypadNum')}
                            ref={(el: HTMLInputElement) => {
                                const originRef = fields.keypadNum.inputProps.ref;
                                originRef(el);

                                secureKeypad.ref?.(el);
                            }}
                        />
                    </FieldWrapper>
                    <FieldWrapper
                        labelFor="birthNum"
                        title={t('SVCSPD001_11TB.008', '생년월일')}
                        // isError={isError('birthNum')}
                        // helpMessage={getFieldError('birthNum')}
                    >
                        <FieldRound
                            // {...fields.birthNum.inputProps}
                            id="birthNum"
                            inputMode="numeric"
                            maxLength={6}
                            placeholder={t('SVCSPD001_11TB.009', '숫자 6자리')}
                            title={t('SVCSPD001_11TB.009', '숫자 6자리')}
                            value={birthNumValue}
                            ref={birthNumRef}
                            onChange={(e) => {
                                const onlyNumbers = e.target.value.replace(/[^0-9]/g, '');

                                fields.birthNum.inputProps.onChange?.({
                                    ...e,
                                    target: {
                                        ...e.target,
                                        value: onlyNumbers,
                                    },
                                });

                                setBirthNumValue(onlyNumbers);
                            }}
                        />
                    </FieldWrapper>
                    <FieldWrapper title={t('SVCSPD001_11TB.010', '조회기간')}>
                        <NativeSelect
                            id="pickerDate"
                            aria-label={t('SVCSPD001_11TB.054', '조회기간 선택')}
                            onChange={(e) => setTerm(e.target.value)}
                        >
                            <NativeSelectOption value={1}>
                                {t('SVCSPD001_11TB.011', '오늘')}
                            </NativeSelectOption>
                            <NativeSelectOption value={2}>
                                {t('SVCSPD001_11TB.012', '3일')}
                            </NativeSelectOption>
                            <NativeSelectOption value={3}>
                                {t('SVCSPD001_11TB.013', '1주')}
                            </NativeSelectOption>
                            <NativeSelectOption value={4}>
                                {t('SVCSPD001_11TB.014', '1개월')}
                            </NativeSelectOption>
                            <NativeSelectOption value={5}>
                                {t('SVCSPD001_11TB.015', '3개월')}
                            </NativeSelectOption>
                            <NativeSelectOption value={6}>
                                {t('SVCSPD001_11TB.016', '기간설정')}
                            </NativeSelectOption>
                        </NativeSelect>
                        {term === '6' && (
                            <DatePickerRange
                                variants="day"
                                startDate={rangeDate?.start ?? ''}
                                endDate={rangeDate?.end ?? ''}
                                onChange={(next) => {
                                    setRangeDate({
                                        ...rangeDate,
                                        start: next.start,
                                        end: next.end,
                                    });
                                }}
                                startYear={new Date(currYear - 10, 0)}
                                endYear={new Date(currYear, 11)}
                                disabledFrom={(date) => {
                                    return date > new Date(rangeDate?.limitEnd ?? '');
                                }}
                                disabledTo={(date) => {
                                    return date > new Date(rangeDate?.limitStart ?? '');
                                }}
                                mode="picker"
                                // readonlyFrom
                                // readonlyTo
                            />
                        )}
                    </FieldWrapper>
                    <div className="flex items-center justify-center">
                        <Button
                            size="md"
                            variant="outline"
                            onClick={searchBtn}
                            disabled={
                                isLoadingGetInitTransactionData ||
                                isLoadingGetInitDemandTransactionData ||
                                isLoadingGetInitForeignTransactionData
                            }
                        >
                            {t('SVCSPD001_11TB.018', '조회')}
                            <LineIcon
                                icoColor="bg-icon-primary-04"
                                icoName="ico-search"
                                type="xs"
                            />
                        </Button>
                    </div>

                    {flag !== '' && (
                        <>
                            <div className="mt-2">
                                {/* 260407 버튼 사이즈 변경 */}
                                <div className="flex items-center justify-end gap-2">
                                    <Button
                                        size="xs"
                                        variant="outline"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handlePrintClick();
                                        }}
                                        title={t('SVCSPD001_11TB.074', '새창열림')}
                                    >
                                        {t('SVCSPD001_11TB.019', '인쇄')}
                                    </Button>
                                    <Button
                                        size="xs"
                                        variant="outline"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handleDownloadClick();
                                        }}
                                    >
                                        {t('SVCSPD001_11TB.020', '엑셀저장')}
                                    </Button>
                                </div>

                                {/* 260407 테이블 스타일 변경 S */}
                                <div ref={mergeRef} className="print-main">
                                    <Table
                                        ref={tableRef2}
                                        className="print-table__col border-t border-basic-02 mt-4"
                                    >
                                        <colgroup>
                                            <col className="w-[150px]"></col>
                                            <col className=""></col>
                                        </colgroup>
                                        <TableCaption>
                                            {t('SVCSPD001_11TB.056', '조회 요약정보 테이블')}
                                        </TableCaption>
                                        <TableBody>
                                            {/* 적립식 예금, 펀드, 61 */}
                                            {(flag === '4' || flag === '1') && (
                                                <>
                                                    <TableRow>
                                                        <TableHead
                                                            scope="row"
                                                            className="text-left px-4"
                                                        >
                                                            {t('SVCSPD001_11TB.021', '조회기간')}
                                                        </TableHead>
                                                        <TableCell
                                                            className={cn(
                                                                'border-l border-basic-05 text-left', //260417 클래스 추가
                                                            )}
                                                        >
                                                            {formatUtils.toDate(
                                                                initData?.referStartDay ?? '',
                                                                '.',
                                                            )}{' '}
                                                            ~{' '}
                                                            {formatUtils.toDate(
                                                                initData?.referEndDay ?? '',
                                                                '.',
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                    <TableRow>
                                                        <TableHead
                                                            scope="row"
                                                            className="text-left px-4"
                                                        >
                                                            {t('SVCSPD001_11TB.022', '계좌번호')}
                                                        </TableHead>
                                                        <TableCell
                                                            className={cn(
                                                                'border-l border-basic-05 text-left', //260417 클래스 추가
                                                            )}
                                                        >
                                                            {formatUtils.formatHyphen(
                                                                initData?.acctNum ?? '',
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                    <TableRow>
                                                        <TableHead
                                                            scope="row"
                                                            className="text-left px-4"
                                                        >
                                                            {t('SVCSPD001_11TB.057', '펀드번호')}
                                                        </TableHead>
                                                        <TableCell
                                                            className={cn(
                                                                'border-l border-basic-05 text-left', //260417 클래스 추가
                                                            )}
                                                        >
                                                            {initData?.fundCode}
                                                        </TableCell>
                                                    </TableRow>
                                                    <TableRow>
                                                        <TableHead
                                                            scope="row"
                                                            className="text-left px-4"
                                                        >
                                                            {t('SVCSPD001_11TB.023', '상품명')}
                                                        </TableHead>
                                                        <TableCell
                                                            className={cn(
                                                                'border-l border-basic-05 text-left', //260417 클래스 추가
                                                            )}
                                                        >
                                                            {initData?.fundName}
                                                        </TableCell>
                                                    </TableRow>
                                                    <TableRow>
                                                        <TableHead
                                                            scope="row"
                                                            className="text-left px-4"
                                                        >
                                                            {t('SVCSPD001_11TB.058', '펀드종별')}
                                                        </TableHead>
                                                        <TableCell
                                                            className={cn(
                                                                'border-l border-basic-05 text-left', //260417 클래스 추가
                                                            )}
                                                        >
                                                            {initData?.depItemZong}
                                                        </TableCell>
                                                    </TableRow>
                                                    <TableRow>
                                                        <TableHead
                                                            scope="row"
                                                            className="text-left px-4"
                                                        >
                                                            {t('SVCSPD001_11TB.059', '통화명')}
                                                        </TableHead>
                                                        <TableCell
                                                            className={cn(
                                                                'border-l border-basic-05 text-left', //260417 클래스 추가
                                                            )}
                                                        >
                                                            {initData?.currencyName}
                                                        </TableCell>
                                                    </TableRow>
                                                    <TableRow>
                                                        <TableHead
                                                            scope="row"
                                                            className="text-left px-4"
                                                        >
                                                            {t('SVCSPD001_11TB.024', '예금주')}
                                                        </TableHead>
                                                        <TableCell
                                                            className={cn(
                                                                'border-l border-basic-05 text-left', //260417 클래스 추가
                                                            )}
                                                        >
                                                            {initData?.custName}
                                                        </TableCell>
                                                    </TableRow>
                                                    <TableRow>
                                                        <TableHead
                                                            scope="row"
                                                            className="text-left px-4"
                                                        >
                                                            {t('SVCSPD001_11TB.060', '평가금액')}
                                                        </TableHead>
                                                        <TableCell
                                                            className={cn(
                                                                'border-l border-basic-05 text-right', //260417 클래스 추가
                                                            )}
                                                        >
                                                            {initData?.currencyCode === '999' ? (
                                                                <>
                                                                    {initData?.currencyName}{' '}
                                                                    {formatUtils.getNumberFormat(
                                                                        String(
                                                                            initData?.hungBalance,
                                                                        ),
                                                                        100,
                                                                        '#,##0',
                                                                    )}
                                                                </>
                                                            ) : (
                                                                <>
                                                                    {initData?.currencyName}{' '}
                                                                    {formatUtils.getNumberFormat(
                                                                        String(
                                                                            initData?.hungBalance,
                                                                        ),
                                                                        1,
                                                                        '#,##0.00',
                                                                    )}
                                                                </>
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                </>
                                            )}
                                            {/* 요구불 */}
                                            {flag === '2' && (
                                                <>
                                                    <TableRow>
                                                        <TableHead
                                                            scope="row"
                                                            className="text-left px-4"
                                                        >
                                                            {t('SVCSPD001_11TB.021', '조회기간')}
                                                        </TableHead>
                                                        <TableCell
                                                            className={cn(
                                                                'border-l border-basic-05 text-left', //260417 클래스 추가
                                                            )}
                                                        >
                                                            {formatUtils.toDate(
                                                                initDepositData?.referStartDay ??
                                                                    '',
                                                                '.',
                                                            )}{' '}
                                                            ~{' '}
                                                            {formatUtils.toDate(
                                                                initDepositData?.referEndDay ?? '',
                                                                '.',
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                    <TableRow>
                                                        <TableHead
                                                            scope="row"
                                                            className="text-left px-4"
                                                        >
                                                            {t('SVCSPD001_11TB.022', '계좌번호')}
                                                        </TableHead>
                                                        <TableCell
                                                            className={cn(
                                                                'border-l border-basic-05 text-left', //260417 클래스 추가
                                                            )}
                                                        >
                                                            {formatUtils.formatHyphen(
                                                                initDepositData?.acctNum ?? '',
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                    <TableRow>
                                                        <TableHead
                                                            scope="row"
                                                            className="text-left px-4"
                                                        >
                                                            {t('SVCSPD001_11TB.023', '상품명')}
                                                        </TableHead>
                                                        <TableCell
                                                            className={cn(
                                                                'border-l border-basic-05 text-left', //260417 클래스 추가
                                                            )}
                                                        >
                                                            {initDepositData?.depItemName}
                                                        </TableCell>
                                                    </TableRow>
                                                    <TableRow>
                                                        <TableHead
                                                            scope="row"
                                                            className="text-left px-4"
                                                        >
                                                            {t('SVCSPD001_11TB.024', '예금주')}
                                                        </TableHead>
                                                        <TableCell
                                                            className={cn(
                                                                'border-l border-basic-05 text-left', //260417 클래스 추가
                                                            )}
                                                        >
                                                            {initDepositData?.custName}
                                                        </TableCell>
                                                    </TableRow>
                                                    <TableRow>
                                                        <TableHead
                                                            scope="row"
                                                            className="text-left px-4"
                                                        >
                                                            {t('SVCSPD001_11TB.025', '계좌잔액')}
                                                        </TableHead>
                                                        <TableCell
                                                            className={cn(
                                                                'border-l border-basic-05 text-left', //260417 클래스 추가
                                                            )}
                                                        >
                                                            {initDepositData?.nomiBalSign === '-'
                                                                ? initDepositData.nomiBalSign
                                                                : ''}
                                                            {formatUtils.getNumberFormat(
                                                                String(
                                                                    initDepositData?.nomiBalance,
                                                                ),
                                                                1,
                                                                '#,##0',
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                    <TableRow>
                                                        <TableHead
                                                            scope="row"
                                                            className="text-left px-4"
                                                        >
                                                            {t(
                                                                'SVCSPD001_11TB.026',
                                                                '지불가능금액',
                                                            )}
                                                        </TableHead>
                                                        <TableCell
                                                            className={cn(
                                                                'border-l border-basic-05 text-right', //260417 클래스 추가
                                                            )}
                                                        >
                                                            {initDepositData?.availBalSign === '-'
                                                                ? initDepositData.availBalSign
                                                                : ''}
                                                            {formatUtils.getNumberFormat(
                                                                String(
                                                                    initDepositData?.availBalance,
                                                                ),
                                                                1,
                                                                '#,##0',
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                </>
                                            )}

                                            {/* 외화명세 */}
                                            {flag === '3' && (
                                                <>
                                                    <TableRow>
                                                        <TableHead
                                                            scope="row"
                                                            className="text-left px-4"
                                                        >
                                                            {t('SVCSPD001_11TB.021', '조회기간')}
                                                        </TableHead>
                                                        <TableCell
                                                            className={cn(
                                                                'border-l border-basic-05 text-left', //260417 클래스 추가
                                                            )}
                                                        >
                                                            {formatUtils.toDate(
                                                                initForeignData?.startDate ?? '',
                                                                '.',
                                                            )}{' '}
                                                            ~{' '}
                                                            {formatUtils.toDate(
                                                                initForeignData?.endDate ?? '',
                                                                '.',
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                    <TableRow>
                                                        <TableHead
                                                            scope="row"
                                                            className="text-left px-4"
                                                        >
                                                            {t('SVCSPD001_11TB.022', '계좌번호')}
                                                        </TableHead>
                                                        <TableCell
                                                            className={cn(
                                                                'border-l border-basic-05 text-left', //260417 클래스 추가
                                                            )}
                                                        >
                                                            {formatUtils.formatHyphen(
                                                                initForeignData?.acctNum ?? '',
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                    <TableRow>
                                                        <TableHead
                                                            scope="row"
                                                            className="text-left px-4"
                                                        >
                                                            {t('SVCSPD001_11TB.023', '상품명')}
                                                        </TableHead>
                                                        <TableCell
                                                            className={cn(
                                                                'border-l border-basic-05 text-left', //260417 클래스 추가
                                                            )}
                                                        >
                                                            {initForeignData?.depItemName}
                                                        </TableCell>
                                                    </TableRow>
                                                    <TableRow>
                                                        <TableHead
                                                            scope="row"
                                                            className="text-left px-4"
                                                        >
                                                            {t('SVCSPD001_11TB.024', '예금주')}
                                                        </TableHead>
                                                        <TableCell
                                                            className={cn(
                                                                'border-l border-basic-05 text-left', //260417 클래스 추가
                                                            )}
                                                        >
                                                            {initForeignData?.custName}
                                                        </TableCell>
                                                    </TableRow>
                                                    <TableRow>
                                                        <TableHead
                                                            scope="row"
                                                            className="text-left px-4"
                                                        >
                                                            {t('SVCSPD001_11TB.025', '계좌잔액')}
                                                        </TableHead>
                                                        <TableCell
                                                            className={cn(
                                                                'border-l border-basic-05 text-right', //260417 클래스 추가
                                                            )}
                                                        >
                                                            {initForeignData?.currencyName}{' '}
                                                            {initForeignData?.balSign === '-'
                                                                ? initForeignData?.balSign
                                                                : ''}
                                                            {formatUtils.getNumberFormat(
                                                                String(initForeignData?.balance),
                                                                1,
                                                                '#,##0.00',
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                </>
                                            )}
                                        </TableBody>
                                    </Table>

                                    {/* 적립식 예금, 펀드, 61 */}
                                    {(flag === '4' || flag === '1') && (
                                        <div className="mt-4 w-full">
                                            {/* 260417 클래스 삭제 */}
                                            <Table
                                                ref={tableRef}
                                                className="print-table__row border-t border-basic-02 min-w-max"
                                            >
                                                <colgroup>
                                                    <col className="w-[167px]"></col>
                                                    <col className="w-[112px]"></col>
                                                    <col className="w-[112px]"></col>
                                                    <col className="w-[112px]"></col>
                                                    <col className="w-[112px]"></col>
                                                    <col className="w-[112px]"></col>
                                                    <col className="w-[112px]"></col>
                                                </colgroup>
                                                <TableCaption>
                                                    {t(
                                                        'SVCSPD001_11TB.061',
                                                        '거래일시, 기준가, 거래좌수, 지급 원장금액, 입금금액, 내역, 거래점 테이블',
                                                    )}
                                                </TableCaption>
                                                <TableHeader>
                                                    <TableRow>
                                                        <TableHead>
                                                            {t('SVCSPD001_11TB.027', '거래일시')}
                                                        </TableHead>
                                                        <TableHead>
                                                            {t('SVCSPD001_11TB.062', '기준가')}
                                                        </TableHead>
                                                        <TableHead>
                                                            {t('SVCSPD001_11TB.063', '거래좌수')}
                                                        </TableHead>
                                                        <TableHead>
                                                            {t(
                                                                'SVCSPD001_11TB.064',
                                                                '지급 원장금액',
                                                            )}
                                                        </TableHead>
                                                        <TableHead>
                                                            {t('SVCSPD001_11TB.065', '입금금액')}
                                                        </TableHead>
                                                        <TableHead>
                                                            {t('SVCSPD001_11TB.031', '내역')}
                                                        </TableHead>
                                                        <TableHead>
                                                            {t('SVCSPD001_11TB.033', '거래점')}
                                                        </TableHead>
                                                    </TableRow>
                                                </TableHeader>
                                                <TableBody>
                                                    {initDataList?.length ? (
                                                        initDataList.map((data, idx) => {
                                                            let trxCount1 = '';
                                                            let pay = 0;
                                                            let sign = '';
                                                            let trxAmt1 = 0;
                                                            let trxAmt2 = 0;
                                                            let outSumAmt = 0;
                                                            let inSumAmt = 0;
                                                            let dealGb = data?.summary;

                                                            if (initData?.currencyCode === '999') {
                                                                trxCount1 = String(
                                                                    data?.trxCount1,
                                                                ).substring(
                                                                    0,
                                                                    String(data?.trxCount1).length -
                                                                        3,
                                                                );
                                                                pay = Number(
                                                                    String(
                                                                        data?.trxAmtSign,
                                                                    ).substring(
                                                                        0,
                                                                        String(data?.trxAmtSign)
                                                                            .length - 2,
                                                                    ),
                                                                );
                                                            } else {
                                                                trxCount1 = String(data.trxCount1);
                                                                pay = Number(data.trxAmtSign);
                                                            }

                                                            sign = String(pay).substring(0, 1);

                                                            if (data?.depPayPart === '2') {
                                                                if (sign === '-') {
                                                                    trxAmt1 = Number(
                                                                        String(pay).substring(
                                                                            1,
                                                                            String(pay).length,
                                                                        ),
                                                                    );

                                                                    if (
                                                                        dealGb !== '환매신청' &&
                                                                        dealGb !== '환매신청취소'
                                                                    ) {
                                                                        outSumAmt =
                                                                            inSumAmt +
                                                                            Number(
                                                                                String(
                                                                                    pay,
                                                                                ).substring(
                                                                                    1,
                                                                                    String(pay)
                                                                                        .length,
                                                                                ),
                                                                            );
                                                                    }
                                                                } else {
                                                                    trxAmt2 = pay;

                                                                    if (
                                                                        dealGb !== '환매신청' &&
                                                                        dealGb !== '환매신청취소'
                                                                    ) {
                                                                        outSumAmt = inSumAmt - pay;
                                                                    }
                                                                }
                                                            } else {
                                                                if (sign === '-') {
                                                                    trxAmt1 = Number(
                                                                        String(pay).substring(
                                                                            1,
                                                                            String(pay).length,
                                                                        ),
                                                                    );

                                                                    if (
                                                                        dealGb !== '입금예약' &&
                                                                        dealGb !== '입금예약취소'
                                                                    ) {
                                                                        inSumAmt =
                                                                            outSumAmt -
                                                                            Number(
                                                                                String(
                                                                                    pay,
                                                                                ).substring(
                                                                                    1,
                                                                                    String(pay)
                                                                                        .length,
                                                                                ),
                                                                            );
                                                                    }
                                                                } else {
                                                                    trxAmt2 = pay;

                                                                    if (
                                                                        dealGb !== '환매신청' &&
                                                                        dealGb !== '환매신청취소'
                                                                    ) {
                                                                        inSumAmt = outSumAmt + pay;
                                                                    }
                                                                }
                                                            }

                                                            return (
                                                                <TableRow key={idx}>
                                                                    <TableCell>
                                                                        {formatUtils.toDate(
                                                                            data.trxDate,
                                                                            '.',
                                                                        )}
                                                                    </TableCell>
                                                                    <TableCell className="text-right">
                                                                        {formatUtils.getNumberFormat(
                                                                            String(data.criterAmt),
                                                                            1,
                                                                            '#,##0.00',
                                                                        )}
                                                                    </TableCell>
                                                                    <TableCell className="text-right">
                                                                        {initData?.currencyCode ===
                                                                        '999'
                                                                            ? formatUtils.getNumberFormat(
                                                                                  trxCount1,
                                                                                  1,
                                                                                  '#,##0',
                                                                              )
                                                                            : formatUtils.getNumberFormat(
                                                                                  trxCount1,
                                                                                  1,
                                                                                  '#,##0.000',
                                                                              )}
                                                                    </TableCell>
                                                                    <TableCell className="text-right">
                                                                        {initData?.currencyCode ===
                                                                        '999'
                                                                            ? formatUtils.getNumberFormat(
                                                                                  String(trxAmt1),
                                                                                  1,
                                                                                  '#,##0',
                                                                              )
                                                                            : formatUtils.getNumberFormat(
                                                                                  String(trxAmt1),
                                                                                  1,
                                                                                  '#,##0.00',
                                                                              )}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {initData?.currencyCode ===
                                                                        '999'
                                                                            ? formatUtils.getNumberFormat(
                                                                                  String(trxAmt2),
                                                                                  1,
                                                                                  '#,##0',
                                                                              )
                                                                            : formatUtils.getNumberFormat(
                                                                                  String(trxAmt2),
                                                                                  100,
                                                                                  '#,##0.00',
                                                                              )}
                                                                    </TableCell>
                                                                    <TableCell>{dealGb}</TableCell>
                                                                    <TableCell>
                                                                        {data.branchName}
                                                                    </TableCell>
                                                                </TableRow>
                                                            );
                                                        })
                                                    ) : (
                                                        <TableRow>
                                                            <TableCell
                                                                colSpan={7}
                                                                className="text-center"
                                                            >
                                                                {t(
                                                                    'SVCSPD001_11TB.066',
                                                                    '조회기간 중 거래내역이 없어요.',
                                                                )}
                                                            </TableCell>
                                                        </TableRow>
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </div>
                                    )}
                                    {/* 요구불 */}
                                    {flag === '2' && (
                                        <div className="mt-4 w-full">
                                            {/* 260417 클래스 삭제 */}
                                            <Table
                                                ref={tableRef}
                                                className="print-table__row border-t border-basic-02 min-w-max"
                                            >
                                                <colgroup>
                                                    <col className="w-[167px]"></col>
                                                    <col className="w-[112px]"></col>
                                                    <col className="w-[112px]"></col>
                                                    <col className="w-[112px]"></col>
                                                    <col className="w-[112px]"></col>
                                                    <col className="w-[112px]"></col>
                                                    <col className="w-[112px]"></col>
                                                </colgroup>
                                                <TableCaption>
                                                    {t(
                                                        'SVCSPD001_11TB.067',
                                                        '거래일시, 찾으신 금액, 맡기신 금액, 잔액, 내역, 내역, 적요, 거래점 테이블',
                                                    )}
                                                </TableCaption>
                                                <TableHeader>
                                                    <TableRow>
                                                        <TableHead>
                                                            {t('SVCSPD001_11TB.027', '거래일시')}
                                                        </TableHead>
                                                        <TableHead>
                                                            {t('SVCSPD001_11TB.028', '찾으신 금액')}
                                                        </TableHead>
                                                        <TableHead>
                                                            {t('SVCSPD001_11TB.029', '맡기신 금액')}
                                                        </TableHead>
                                                        <TableHead>
                                                            {t('SVCSPD001_11TB.030', '잔액')}
                                                        </TableHead>
                                                        <TableHead>
                                                            {t('SVCSPD001_11TB.031', '내역')}
                                                        </TableHead>
                                                        <TableHead>
                                                            {t('SVCSPD001_11TB.032', '적요')}
                                                        </TableHead>
                                                        <TableHead>
                                                            {t('SVCSPD001_11TB.033', '거래점')}
                                                        </TableHead>
                                                    </TableRow>
                                                </TableHeader>
                                                <TableBody>
                                                    {initDepositDataList?.length ? (
                                                        initDepositDataList.map((data, idx) => {
                                                            return (
                                                                <TableRow key={idx}>
                                                                    <TableCell>
                                                                        {formatUtils.toDate(
                                                                            data.trxDate,
                                                                            '.',
                                                                        )}{' '}
                                                                        {formatUtils.toTime(
                                                                            data.trxTime,
                                                                            ':',
                                                                        )}
                                                                    </TableCell>
                                                                    <TableCell className="text-right">
                                                                        {data.depPayPart === '2'
                                                                            ? formatUtils.getNumberFormat(
                                                                                  String(
                                                                                      data.trxAmt,
                                                                                  ),
                                                                                  1,
                                                                                  '#,##0',
                                                                              )
                                                                            : 0}
                                                                    </TableCell>
                                                                    <TableCell className="text-right">
                                                                        {data.depPayPart === '2'
                                                                            ? 0
                                                                            : formatUtils.getNumberFormat(
                                                                                  String(
                                                                                      data.trxAmt,
                                                                                  ),
                                                                                  1,
                                                                                  '#,##0',
                                                                              )}
                                                                    </TableCell>
                                                                    <TableCell className="text-right">
                                                                        {data.trxAfterBalSign ===
                                                                        '-'
                                                                            ? data.trxAfterBalSign
                                                                            : ''}
                                                                        {formatUtils.getNumberFormat(
                                                                            String(
                                                                                data.trxAfterBalance,
                                                                            ),
                                                                            1,
                                                                            '#,##0',
                                                                        )}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {data.summary1}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {data.summary2}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {data.branchName}
                                                                    </TableCell>
                                                                </TableRow>
                                                            );
                                                        })
                                                    ) : (
                                                        <TableRow>
                                                            <TableCell
                                                                colSpan={7}
                                                                className="text-center"
                                                            >
                                                                {t(
                                                                    'SVCSPD001_11TB.066',
                                                                    '조회기간 중 거래내역이 없어요.',
                                                                )}
                                                            </TableCell>
                                                        </TableRow>
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </div>
                                    )}

                                    {/* 외화명세 */}
                                    {flag === '3' && (
                                        <div className="mt-4 w-full">
                                            {/* 260417 클래스 삭제 */}
                                            <Table
                                                ref={tableRef}
                                                className="print-table__row border-t border-basic-02 min-w-max"
                                            >
                                                <colgroup>
                                                    <col className="w-[167px]"></col>
                                                    <col className="w-[112px]"></col>
                                                    <col className="w-[112px]"></col>
                                                    <col className="w-[112px]"></col>
                                                    <col className="w-[112px]"></col>
                                                    <col className="w-[112px]"></col>
                                                    <col className="w-[112px]"></col>
                                                </colgroup>
                                                <TableCaption>
                                                    {t(
                                                        'SVCSPD001_11TB.068',
                                                        '거래일시, 찾으신 금액, 맡기신 금액, 환율, 잔액, 적요, 거래점 테이블',
                                                    )}
                                                </TableCaption>
                                                <TableHeader>
                                                    <TableRow>
                                                        <TableHead>
                                                            {t('SVCSPD001_11TB.027', '거래일시')}
                                                        </TableHead>
                                                        <TableHead>
                                                            {t('SVCSPD001_11TB.028', '찾으신 금액')}
                                                        </TableHead>
                                                        <TableHead>
                                                            {t('SVCSPD001_11TB.029', '맡기신 금액')}
                                                        </TableHead>
                                                        <TableHead>
                                                            {t('SVCSPD001_11TB.069', '환율')}
                                                        </TableHead>
                                                        <TableHead>
                                                            {t('SVCSPD001_11TB.030', '잔액')}
                                                        </TableHead>
                                                        <TableHead>
                                                            {t('SVCSPD001_11TB.032', '적요')}
                                                        </TableHead>
                                                        <TableHead>
                                                            {t('SVCSPD001_11TB.033', '거래점')}
                                                        </TableHead>
                                                    </TableRow>
                                                </TableHeader>
                                                <TableBody>
                                                    {initForeignDataList?.length ? (
                                                        initForeignDataList.map((data, idx) => {
                                                            return (
                                                                <TableRow key={idx}>
                                                                    <TableCell>
                                                                        {formatUtils.toDate(
                                                                            data.trxDate,
                                                                            '.',
                                                                        )}
                                                                    </TableCell>
                                                                    <TableCell className="text-right">
                                                                        {data.trxPart === '입금' ||
                                                                        data.trxPart ===
                                                                            '예금이자' ||
                                                                        data.trxPart === '원가입금'
                                                                            ? initForeignData?.currencyName +
                                                                              ' 0'
                                                                            : initForeignData?.currencyName +
                                                                              ' ' +
                                                                              formatUtils.getNumberFormat(
                                                                                  String(
                                                                                      data.trxAmt,
                                                                                  ),
                                                                                  1,
                                                                                  '#,##0.00',
                                                                              )}
                                                                    </TableCell>
                                                                    <TableCell className="text-right">
                                                                        {data.trxPart === '입금' ||
                                                                        data.trxPart ===
                                                                            '예금이자' ||
                                                                        data.trxPart === '원가입금'
                                                                            ? initForeignData?.currencyName +
                                                                              ' ' +
                                                                              formatUtils.getNumberFormat(
                                                                                  String(
                                                                                      data.trxAmt,
                                                                                  ),
                                                                                  1,
                                                                                  '#,##0.00',
                                                                              )
                                                                            : initForeignData?.currencyName +
                                                                              ' 0'}
                                                                    </TableCell>
                                                                    <TableCell className="text-right">
                                                                        {formatUtils.getNumberFormat(
                                                                            String(data.exchRate),
                                                                            1,
                                                                            '#,##0.00',
                                                                        )}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        {
                                                                            initForeignData?.currencyName
                                                                        }{' '}
                                                                        {data.positionSign === '-'
                                                                            ? data.positionSign +
                                                                              formatUtils.getNumberFormat(
                                                                                  String(
                                                                                      data.positionAmt,
                                                                                  ),
                                                                                  1,
                                                                                  '#,##0.00',
                                                                              )
                                                                            : formatUtils.getNumberFormat(
                                                                                  String(
                                                                                      data.positionAmt,
                                                                                  ),
                                                                                  1,
                                                                                  '#,##0.00',
                                                                              )}
                                                                    </TableCell>
                                                                    <TableCell>{}</TableCell>
                                                                    <TableCell>{}</TableCell>
                                                                </TableRow>
                                                            );
                                                        })
                                                    ) : (
                                                        <TableRow>
                                                            <TableCell
                                                                colSpan={7}
                                                                className="text-center"
                                                            >
                                                                {t(
                                                                    'SVCSPD001_11TB.066',
                                                                    '조회기간 중 거래내역이 없어요.',
                                                                )}
                                                            </TableCell>
                                                        </TableRow>
                                                    )}
                                                </TableBody>
                                            </Table>
                                        </div>
                                    )}
                                    {/* 260407 테이블 스타일 변경 E */}

                                    {/* 요구불 */}
                                    {flag === '2' && (
                                        <>
                                            {(initDepositData?.processTp ?? '') === 'N' ? (
                                                <div className="print-hidden flex items-center justify-center mt-4">
                                                    <ButtonWithIcon
                                                        title={t('SVCSPD001_11TB.071', '더보기')}
                                                        icon="more"
                                                        direction="right"
                                                        onClick={() => {
                                                            serchAddBtn('1');
                                                        }}
                                                        disabled={
                                                            isLoadingGetInitDemandTransactionData
                                                        }
                                                    />
                                                </div>
                                            ) : (
                                                <div className="print-hidden end-of-list">
                                                    {t(
                                                        'SVCSPD001_11TB.072',
                                                        '거래내역을 모두 조회했어요.',
                                                    )}
                                                </div>
                                            )}

                                            <div ref={tableRef3}>
                                                <ol className="total mt-3 text-right">
                                                    <li className="body-md text-basic-04">
                                                        {t('SVCSPD001_11TB.034', '입금합계')}
                                                        <strong className="text-primary-04 ml-2">
                                                            {formatUtils.getNumberFormat(
                                                                String(inSumAmt),
                                                                1,
                                                                '#,##0',
                                                            )}
                                                            {t('SVCSPD001_11TB.070', '원')}
                                                        </strong>
                                                    </li>
                                                    <li className="body-md text-basic-04">
                                                        {t('SVCSPD001_11TB.035', '출금합계')}
                                                        <strong className="text-state-danger ml-2">
                                                            {formatUtils.getNumberFormat(
                                                                String(outSumAmt),
                                                                1,
                                                                '#,##0',
                                                            )}
                                                            {t('SVCSPD001_11TB.070', '원')}
                                                        </strong>
                                                    </li>
                                                </ol>
                                            </div>
                                        </>
                                    )}

                                    {/* 적립식 예금, 펀드, 61 */}
                                    {(flag === '4' || flag === '1') && (
                                        <>
                                            {(initData?.processTp ?? '') === 'N' ? (
                                                <div className="print-hidden flex items-center justify-center mt-4">
                                                    <ButtonWithIcon
                                                        title={t('SVCSPD001_11TB.071', '더보기')}
                                                        icon="more"
                                                        direction="right"
                                                        onClick={() => {
                                                            serchAddBtn('2');
                                                        }}
                                                        disabled={isLoadingGetInitTransactionData}
                                                    />
                                                </div>
                                            ) : (
                                                <div className="print-hidden end-of-list">
                                                    {t(
                                                        'SVCSPD001_11TB.072',
                                                        '거래내역을 모두 조회했어요.',
                                                    )}
                                                </div>
                                            )}
                                        </>
                                    )}

                                    {/* 외화명세 */}
                                    {flag === '3' && (
                                        <>
                                            {(initForeignData?.processTp ?? '') === 'N' ? (
                                                <div className="print-hidden flex items-center justify-center mt-4">
                                                    <ButtonWithIcon
                                                        title={t('SVCSPD001_11TB.071', '더보기')}
                                                        icon="more"
                                                        direction="right"
                                                        onClick={() => {
                                                            serchAddBtn('3');
                                                        }}
                                                        disabled={
                                                            isLoadingGetInitForeignTransactionData
                                                        }
                                                    />
                                                </div>
                                            ) : (
                                                <div className="print-hidden end-of-list">
                                                    {t(
                                                        'SVCSPD001_11TB.072',
                                                        '거래내역을 모두 조회했어요.',
                                                    )}
                                                </div>
                                            )}

                                            <div ref={tableRef3}>
                                                <ol className="total mt-3 text-right">
                                                    <li className="body-md text-basic-04">
                                                        {t('SVCSPD001_11TB.034', '입금합계')}
                                                        <strong className="text-primary-04 ml-2">
                                                            {initForeignData?.currencyName}
                                                            {formatUtils.getNumberFormat(
                                                                String(inForeignSumAmt),
                                                                1,
                                                                '#,##0.00',
                                                            )}
                                                        </strong>
                                                    </li>
                                                    <li className="body-md text-basic-04">
                                                        {t('SVCSPD001_11TB.035', '출금합계')}
                                                        <strong className="text-state-danger ml-2">
                                                            {initForeignData?.currencyName}
                                                            {formatUtils.getNumberFormat(
                                                                String(outForeignSumAmt),
                                                                1,
                                                                '#,##0.00',
                                                            )}
                                                        </strong>
                                                    </li>
                                                </ol>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </div>
                <AccordionUnder openTitle={t('SVCSPD001_11TB.036', '알아두세요')}>
                    <BulletList type="circle">
                        <BulletListItem>
                            {t(
                                'SVCSPD001_11TB.037',
                                '통장미기장 거래내역이나 1주 ~ 1개월까지의 거래내역을 300건까지 조회할 수 있어요.',
                            )}
                        </BulletListItem>
                        <BulletListItem>
                            {t(
                                'SVCSPD001_11TB.038',
                                '통장기장이나 미기장 및 거래건수에 따라 조회가능 기간이 달라질 수 있어요.',
                            )}
                        </BulletListItem>
                        <BulletListItem>
                            {t(
                                'SVCSPD001_11TB.039',
                                '더 많은 거래내역 조회를 원하시면 PC뱅킹을 이용해 주세요.(법인은 비즈니스뱅킹 이용 필수)',
                            )}
                        </BulletListItem>
                        <BulletListItem>
                            {t('SVCSPD001_11TB.040', '휴일에는 최근 100건까지만 조회할 수 있어요.')}
                        </BulletListItem>
                        <BulletListItem>
                            {t(
                                'SVCSPD001_11TB.041',
                                '전자금융거래 보안 강화 대책에 따라 스피드조회를 이용할 수 있도록 사전 등록된 계좌만 조회할 수 있어요.',
                            )}
                        </BulletListItem>
                        <BulletListItem>
                            {t('SVCSPD001_11TB.042', '고객 구분에 따른 사전 등록 방법')}
                            <BulletList type="dash" size="smaller">
                                <BulletListItem>
                                    {t(
                                        'SVCSPD001_11TB.043',
                                        'PC뱅킹 미가입고객: 영업점에서 신청(신분확인증표 지참)',
                                    )}
                                </BulletListItem>
                                <BulletListItem>
                                    {t('SVCSPD001_11TB.044', 'PC뱅킹: [계좌 > 스피드조회 등록]')}
                                </BulletListItem>
                            </BulletList>
                        </BulletListItem>
                    </BulletList>
                </AccordionUnder>
            </>
        );
    },
);
